import { useEffect, useState } from 'react';

import { Button, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import PageLayout from 'src/components/PageLayout';
import {
  ButtonsWrapper,
  ContentWrapper,
  EditIcon,
  PageContent,
  SaveButton,
  StyledNameInput,
  TabsStyled,
  TitleStyled,
  TitleWrapper,
} from 'src/components/SharedDetailsComponents';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { useGlobal } from 'src/contexts/GlobalContext';
import { useTheme } from 'src/contexts/ThemeContext';

import { ArrowLeftOutlined } from '@ant-design/icons';

import { LinkButton } from '../CommonStyledComponents/CommonStyledComponents.styled';
import MobileDialog from '../MobileDialog';
import { useModal } from 'src/hooks/useModal';

interface TabItem {
  key: string;
  label: string;
  Component: React.ReactNode;
}

interface TabPagesProps {
  action?: React.ReactNode;
  tabItems: TabItem[];
  tab?: string;
  loading?: boolean;
  renderTitle?: () => React.ReactNode;
  handleTabChange: (key: string) => void;
  items?: TabItem[];
  handleCancel?: () => void;
  handleSave?: () => void | Promise<void> | undefined;
  isDirty?: boolean;
  isUpdateGroupPending?: boolean;
  isPending?: boolean;
  handleUpdateName?: (name: string) => void;
  handleNavigateBack?: () => void;
  titleName?: string;
  activeTabKey?: string;
  cancelButtonText?: string;
  saveButtonText?: string;
  title?: React.ReactNode;
  handleReset: () => void;
  contentWrapperStyle?: React.CSSProperties;
  isDisabled?: boolean;
}

export const TabPages = (props: TabPagesProps) => {
  const {
    action,
    tabItems,
    tab,
    loading,
    handleCancel,
    handleSave,
    isDirty,
    isPending,
    handleUpdateName,
    handleNavigateBack,
    titleName,
    handleTabChange,
    cancelButtonText,
    saveButtonText,
    title,
    handleReset,
    contentWrapperStyle,
    isDisabled,
  } = props;
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(titleName ?? '');
  const activeTabKey = tab || tabItems[0]?.key;
  const { combinedTheme } = useTheme();
  const { confirm, contextHolder } = useModal();

  const handleChangeName = () => {
    if (titleName && titleName !== name) {
      handleUpdateName?.(name);
    }
    setIsEditing(false);
  };

  useEffect(() => {
    setName(titleName ?? '');
  }, [titleName]);

  if (loading) {
    return <SuspenseLoader />;
  }

  const renderContent = () => {
    if (!tab || !tabItems) return null;
    const tabItem = tabItems.find((item) => item.key === tab);
    if (!tabItem) {
      return null;
    }
    const { Component } = tabItem;
    return Component;
  };

  const navigateBack = () => {
    if (isEditing) {
      setIsEditing(false);
    } else {
      handleNavigateBack?.();
    }
  };

  const renderTitle = () =>
    title ||
    (isEditing && handleUpdateName ? (
      <TitleWrapper>
        <LinkButton size="middle" onClick={() => setIsEditing(false)} type="link" icon={<ArrowLeftOutlined />} />
        <StyledNameInput
          placeholder={t('devices.groups.addGroup.namePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <SaveButton loading={isPending} onClick={() => handleChangeName()}>
          {t('devices.groups.addGroup.save')}
        </SaveButton>
      </TitleWrapper>
    ) : (
      <Flex>
        <LinkButton size="middle" onClick={navigateBack} type="link" icon={<ArrowLeftOutlined />}>
          <TitleStyled $entityName={titleName ?? ''}>{titleName}</TitleStyled>
        </LinkButton>
        {handleUpdateName && <EditIcon onClick={() => setIsEditing(true)} />}
      </Flex>
    ));

  const areMobileButtonsVisible = handleCancel && handleSave && !isDesktop;

  const handleResetForm = (key: string, instance?: ReturnType<typeof confirm>) => {
    handleReset();
    if (instance) {
      instance.destroy();
    }
    handleTabChange(key);
  };

  const onTabChange = (key: string) => {
    if (isDirty) {
      if (isDesktop) {
        const instance = confirm({
          title: t('devices.details.settings.unsavedChanges'),
          content: t('devices.details.settings.unsavedChangesDescription'),
          onOk: () => handleResetForm(key, instance),
        });
      } else {
        return MobileDialog({
          theme: combinedTheme,
          title: t('devices.details.settings.unsavedChanges'),
          description: t('devices.details.settings.unsavedChangesDescription'),

          confirmText: t('general.ok').toUpperCase(),
          cancelText: t('general.cancel'),
          onConfirm: () => handleResetForm(key),
        });
      }
    } else {
      handleTabChange(key);
    }
  };

  return (
    <>
      {contextHolder}

      <PageLayout title={renderTitle()} style={{ overflow: 'hidden', paddingTop: 0 }} action={action}>
        <PageContent>
          <TabsStyled
            $isDesktop={isDesktop}
            activeKey={activeTabKey}
            onChange={(key) => onTabChange(key)}
            destroyOnHidden
            items={tabItems}
            $isDisabled={isDisabled}
          />
          <ContentWrapper
            style={contentWrapperStyle}
            $isDesktop={isDesktop}
            $areMobileButtonsVisible={areMobileButtonsVisible}
          >
            {renderContent()}
          </ContentWrapper>
        </PageContent>
        {areMobileButtonsVisible ? (
          <ButtonsWrapper>
            <Button block onClick={handleCancel} disabled={!isDirty}>
              {cancelButtonText}
            </Button>
            <Button type="primary" block disabled={!isDirty} onClick={handleSave} loading={isPending}>
              {saveButtonText}
            </Button>
          </ButtonsWrapper>
        ) : null}
      </PageLayout>
    </>
  );
};

export default TabPages;
